import React, { useCallback, useState } from 'react';

import { useMutation, gql } from '@apollo/client';
import { Formik } from 'formik';
import cn from 'classnames';
import { toast } from 'react-toastify';
import _ from 'lodash';

import Modal from 'Components/Modal/Modal';
import Input from 'Components/Input/Input';
import { logger } from 'RootShared/utility/ErrorLogger';

const mutationSendinvoice = gql`
  mutation sendInvoiceaMutation($emails: [String]!, $message: String!, $pk: Int!, $subject: String!) @api(name: root) {
    sendInvoice(emails: $emails, message: $message, pk: $pk, subject: $subject) {
      result
    }
  }
`;

const SendEmailModal = ({ pk, handleOpenModal }) => {
  const [email, setEmail] = useState('');
  const [errorEmail, setErrorEmail] = useState(false);
  const toastParams = {
    autoClose: 3000,
    closeButton: true,
    hideProgressBar: false,
  };
  const initialValues = { subject: '', message: '', listEmails: [], isFocus: false };

  const onSendEmailMutationSuccess = useCallback(() => {
    toast('Send by email successfully', {
      ...toastParams,
      type: toast.TYPE.SUCCESS,
    });
  }, [toastParams]);

  const onSendEmailMutationError = useCallback(
    (error) => {
      logger.error(error);
      toast(error.message, {
        ...toastParams,
        type: toast.TYPE.ERROR,
      });
    },
    [toastParams]
  );

  const [sendInvoiceaMutation] = useMutation(mutationSendinvoice, {
    onCompleted: onSendEmailMutationSuccess,
    onError: onSendEmailMutationError,
  });

  const validate = (values) => {
    const errors = {};
    if (!values.subject) {
      errors.subject = 'Required';
    }

    if (!values.message) {
      errors.message = 'Required';
    }

    if (values.listEmails.length === 0) {
      errors.listEmails = 'Please add email';
    }

    if (values.isFocus) {
      errors.isFocus = true;
    }

    return errors;
  };

  const handleChangeEmail = useCallback((e) => {
    setEmail(e.target.value);
    setErrorEmail(false);
  }, []);

  const handleDeleteEmail = useCallback((email, values, cb) => {
    const filterListEmail = values.listEmails.filter((item) => email !== item);
    cb({ ...values, listEmails: filterListEmail });
  }, []);

  const handleKeyDown = useCallback((e, values, cbValues) => {
    if (e.key === 'Enter') {
      if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(e.target.value)) {
        setErrorEmail('Enter valid email');
      } else {
        setErrorEmail(false);
        setEmail('');
        const uniqListEmails = _.uniq([...values.listEmails, e.target.value]);
        cbValues({ ...values, listEmails: uniqListEmails });
      }
    }
  }, []);

  const handleSubmit = useCallback(
    (values, setSubmitting, initialValues, resetForm) => {
      if (!errorEmail) {
        sendInvoiceaMutation({
          variables: {
            pk: +pk,
            emails: values.listEmails,
            message: values.message,
            subject: values.subject,
          },
        }).then((data) => {
          setSubmitting(false);
          data && resetForm(initialValues);
        });
      }
    },
    [errorEmail, pk, sendInvoiceaMutation]
  );

  return (
    <Modal>
      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          handleSubmit(values, setSubmitting, initialValues, resetForm);
        }}
      >
        {({ handleSubmit, handleChange, isSubmitting, setValues, values, errors }) => (
          <form onSubmit={handleSubmit}>
            <div className="modal-bg" onClick={(e) => handleOpenModal(e, 'close')}>
              <div className="modal modal-invoice" data-testid="invoice-email-modal">
                <div className="modal__build-head">
                  <h2>Send via email</h2>
                  <span onClick={handleOpenModal} className="icon-close" data-testid="close-mail"/>
                </div>
                <div className="modal__build-info modal_body">
                  <div className="title">Email</div>
                  <div className={cn('input-group__container email_block', { error: errors.listEmails })}>
                    <input
                      placeholder="Add email"
                      className="input-group__field p-0"
                      autoComplete="off"
                      name="listEmails"
                      value={email}
                      data-testid="invoice-email-input"
                      onKeyDown={(e) => handleKeyDown(e, values, setValues)}
                      onChange={handleChangeEmail}
                      onFocus={() => setValues({ ...values, isFocus: true })}
                      onBlur={() => setValues({ ...values, isFocus: false })}
                    />
                    <div className="row_list_emails">
                      {values.listEmails.length > 0 &&
                        values.listEmails.map((item) => (
                          <div key={item} className="block_email">
                            <div>{item}</div>
                            <span className="icon-close" onClick={() => handleDeleteEmail(item, values, setValues)} />
                          </div>
                        ))}
                    </div>
                  </div>
                  <div className={cn('helper', { error_email: errorEmail })}>
                    {errorEmail ? errorEmail : 'To add type email and press "Enter"'}
                  </div>
                  <Input
                    label="Subject"
                    helperVariant={errors.subject ? 'error' : 'message'}
                    fieldProps={{
                      name: 'subject',
                      placeholder: 'Add name subject',
                      value: values.subject,
                      onChange: handleChange,
                    }}
                    id={'subjectInput'}
                  />
                  <Input
                    label="Message"
                    helperVariant={errors.message ? 'error' : 'message'}
                    variant="textarea"
                    fieldProps={{
                      name: 'message',
                      placeholder: 'Add some details',
                      value: values.message,
                    }}
                    callback={(e) => {
                      setValues({ ...values, message: e.target.value });
                    }}
                    id={'messageInput'}
                  />
                </div>
                <div className="modal_footer">
                  <button type="button" className="outlined" onClick={handleOpenModal}>
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className="contained" data-testid="send-email-button">
                    Send
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default SendEmailModal;
